/* eslint-disable camelcase */
import { z } from 'zod'

import { parseResponse, ratingCountsSchema } from './meetingSummaryFeedback'

import { fetchAPI } from '@/api'
import {
  type EvidenceMetadata as EvidenceDTO,
  type GetPrereadFeedbackRatingStatsBucketEnum,
  type GetPrereadFeedbackStatsScopeEnum,
  type ListPrereadFeedbackModelEnum,
  type PrereadFeedbackDetail,
  PrereadFeedbackDetailContentWithheldReasonEnum,
  type PrereadFeedbackList,
  type PrereadFeedbackListItem,
  PrereadFeedbackListItemAssociationEnum,
  PrereadFeedbackListItemRatingEnum,
  PrereadFeedbackListItemSourceEnum,
  type PrereadFeedbackRatingStats,
  type PrereadFeedbackStats,
  PrereadPrefetchStatsRetrievalEnum,
  PrereadPrefetchStatsSourceEnum,
  PrereadResearchStatsOperationEnum,
  PrereadResearchStatsSourceEnum,
  ResearchCallErrorCategoryEnum,
  ResearchCallOperationEnum,
  ResearchCallSourceEnum,
  ResearchCallStatusEnum,
  SourceEvidenceSelectionStatusEnum,
  SourceEvidenceStatusEnum,
} from '@/api/generated/api'

const count = z.number().int().nonnegative()
const timestamp = z.string().datetime({ offset: true })
const source = z.nativeEnum(PrereadFeedbackListItemSourceEnum)
const rating = z.nativeEnum(PrereadFeedbackListItemRatingEnum)
const association = z.nativeEnum(PrereadFeedbackListItemAssociationEnum)
const isSlackReportUrl = (value: string) => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname === 'wispr-ai.slack.com'
  } catch {
    return false
  }
}
const prefetchEntry = z.object({
  status: z.nativeEnum(SourceEvidenceStatusEnum),
  retrieved_count: count.nullable(),
  relevant_count: count.nullable(),
  recent_relevant_count: count.nullable(),
  newest_evidence_age_seconds: count.nullable(),
  truncated: z.boolean().nullable(),
  selection_status: z.nativeEnum(SourceEvidenceSelectionStatusEnum),
})
const researchCall = z.object({
  source: z.nativeEnum(ResearchCallSourceEnum),
  operation: z.nativeEnum(ResearchCallOperationEnum),
  status: z.nativeEnum(ResearchCallStatusEnum),
  error_category: z.nativeEnum(ResearchCallErrorCategoryEnum).nullable(),
  returned_item_count: count.nullable(),
  truncated: z.boolean().nullable(),
})
export const evidenceSchema = z.object({
  version: z.literal(1),
  recent_window_days: z.literal(14),
  prefetch: z.record(z.string(), z.record(z.string(), prefetchEntry)),
  writer_research: z.object({ complete: z.boolean(), calls: z.array(researchCall) }).nullable(),
}) satisfies z.ZodType<EvidenceDTO>
export type EvidenceMetadata = z.infer<typeof evidenceSchema>

export const reportSchema = z.object({
  id: z.guid(),
  rating,
  source,
  reporterEmail: z.string().nullable(),
  feedbackPreview: z.string().nullable(),
  reportedAt: timestamp,
  generationId: z.guid().nullable(),
  association,
  model: z.string().nullable(),
  slackUrl: z.string().url().refine(isSlackReportUrl).nullable(),
}) satisfies z.ZodType<PrereadFeedbackListItem>
export const reportListSchema = z.object({
  items: z.array(reportSchema),
  nextCursor: z.string().nullable(),
  total: count,
  matchedReports: count,
}) satisfies z.ZodType<PrereadFeedbackList>
export const reportDetailSchema = reportSchema.extend({
  feedback: z.string().nullable(),
  externalId: z.string(),
  meetingAt: timestamp.nullable(),
  briefTitle: z.string().nullable(),
  briefContent: z.string().nullable(),
  contentWithheldReason: z.nativeEnum(PrereadFeedbackDetailContentWithheldReasonEnum).nullable(),
  evidenceMetadata: evidenceSchema.nullable(),
}) satisfies z.ZodType<PrereadFeedbackDetail>
const generationCountsSchema = z.object({
  totalGenerations: count,
  successfulGenerations: count,
  emptyGenerations: count,
  failedGenerations: count,
})
export const evidenceStatsSchema = generationCountsSchema.extend({
  sampledGenerations: count,
  diagnosticsTruncated: z.boolean(),
  diagnosticsLimit: count,
  observedGenerations: count,
  unobservedGenerations: count,
  researchObservedGenerations: count,
  researchCompleteGenerations: count,
  totalReports: count,
  matchedReports: count,
  prefetch: z.array(
    z.object({
      source: z.nativeEnum(PrereadPrefetchStatsSourceEnum),
      retrieval: z.nativeEnum(PrereadPrefetchStatsRetrievalEnum),
      observedGenerations: count,
      statusCounts: z.record(z.string(), count),
      retrievedCount: count.nullable(),
      retainedCount: count.nullable(),
      recentCount: count.nullable(),
      retrievedMeasuredGenerations: count,
      retainedMeasuredGenerations: count,
      recentMeasuredGenerations: count,
    })
  ),
  research: z.array(
    z.object({
      source: z.nativeEnum(PrereadResearchStatsSourceEnum),
      operation: z.nativeEnum(PrereadResearchStatsOperationEnum),
      calls: count,
      failedCalls: count,
      returnedItemCount: count.nullable(),
      returnedMeasuredCalls: count,
    })
  ),
  buckets: z.array(generationCountsSchema.extend({ start: timestamp })),
  attemptsPerMeeting: z.array(z.object({ attempts: count, meetings: count })),
}) satisfies z.ZodType<PrereadFeedbackStats>

export const ratingStatsSchema = z.object({
  overall: ratingCountsSchema,
  buckets: z.array(ratingCountsSchema.extend({ start: timestamp })),
}) satisfies z.ZodType<PrereadFeedbackRatingStats>

export type PrereadReport = z.infer<typeof reportSchema>
export type PrereadReportDetail = z.infer<typeof reportDetailSchema>
export type EvidenceStats = z.infer<typeof evidenceStatsSchema>
export type PrereadRatingStats = z.infer<typeof ratingStatsSchema>
export type PrereadRatingBucket = GetPrereadFeedbackRatingStatsBucketEnum
export type PrereadFilters = {
  since: string
  until: string
  source?: z.infer<typeof source>
  rating?: z.infer<typeof rating>
  model?: ListPrereadFeedbackModelEnum
}
export type EvidenceScope = GetPrereadFeedbackStatsScopeEnum
export type PrereadReportFilters = PrereadFilters & { matchedOnly?: boolean }

const BASE = '/api/v1/internal/preread-feedback'
const read = async <T>(
  schema: z.ZodType<T>,
  path: string,
  params?: object,
  signal?: AbortSignal,
  expectedStatuses = [403]
) => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE}${path}`,
    params,
    signal,
    expectedStatuses,
  })
  return parseResponse(schema, response.data, 'Preread feedback')
}

const reportParameters = <T extends PrereadReportFilters>({ matchedOnly, ...filters }: T) => ({
  ...filters,
  ...(matchedOnly === undefined ? {} : { matched_only: matchedOnly }),
})
export const listPrereadFeedback = (filters: PrereadReportFilters & { cursor?: string }, signal?: AbortSignal) =>
  read(reportListSchema, '', { ...reportParameters(filters), limit: 50 }, signal)
export const getPrereadFeedback = (id: string, signal?: AbortSignal) =>
  read(reportDetailSchema, `/${encodeURIComponent(id)}`, undefined, signal, [403, 404])
export const getPrereadEvidenceStats = (
  filters: PrereadFilters & { scope: EvidenceScope; bucket: PrereadRatingBucket },
  signal?: AbortSignal
) => read(evidenceStatsSchema, '/stats', filters, signal)
export const getPrereadRatingStats = (
  filters: PrereadReportFilters & { bucket: PrereadRatingBucket },
  signal?: AbortSignal
) => read(ratingStatsSchema, '/rating-stats', reportParameters(filters), signal)
